import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ChevronDown,
  ChevronRight,
  Users,
  ShoppingBag,
  Star,
  TrendingUp,
  Network,
  DollarSign,
  Activity,
} from 'lucide-react';
import AdminLayout from './AdminLayout';

interface ReferralLeader {
  id: string;
  displayName: string | null;
  username: string;
  avatar: string | null;
  directReferrals: number;
  createdAt: string;
  networkSize: number;
  networkSpend: string;
  networkReviews: number;
  networkOrders: number;
  engagementScore: number;
  engagementRate: string | null;
}

interface NetworkNode {
  id: string;
  displayName: string | null;
  username: string;
  avatar: string | null;
  reviewsCount: number;
  totalOrders: number;
  invitedByUserId: string | null;
  createdAt: string;
  depth: number;
  totalSpend: string;
  orderCount: number;
}

function formatDollar(val: string | number) {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? '$0.00' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function displayName(node: { displayName: string | null; username: string }) {
  return node.displayName || node.username;
}

function depthLabel(depth: number) {
  if (depth === 0) return 'Root';
  if (depth === 1) return 'Direct';
  return `Depth ${depth}`;
}

const DEPTH_COLORS = [
  'bg-primary/10 text-primary border-primary/20',
  'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  'bg-amber-500/10 text-amber-600 border-amber-500/20',
  'bg-violet-500/10 text-violet-600 border-violet-500/20',
  'bg-rose-500/10 text-rose-600 border-rose-500/20',
  'bg-sky-500/10 text-sky-600 border-sky-500/20',
];

function depthBadgeClass(depth: number) {
  return DEPTH_COLORS[depth % DEPTH_COLORS.length];
}

function NetworkTree({ userId }: { userId: string }) {
  const { data: nodes, isLoading } = useQuery<NetworkNode[]>({
    queryKey: ['/api/admin/referral-network', userId],
    queryFn: async () => {
      const _tok = localStorage.getItem('auth_token');
      const res = await fetch(`/api/admin/referral-network/${userId}`, {
        headers: _tok && _tok !== 'null' && _tok !== 'undefined' ? { Authorization: `Bearer ${_tok}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load tree');
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2 mt-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!nodes || nodes.length <= 1) {
    return (
      <p className="text-sm text-muted-foreground mt-3 py-3">No network members found below depth 0.</p>
    );
  }

  const byDepth: Record<number, NetworkNode[]> = {};
  for (const node of nodes) {
    if (node.depth === 0) continue;
    if (!byDepth[node.depth]) byDepth[node.depth] = [];
    byDepth[node.depth].push(node);
  }

  const depths = Object.keys(byDepth)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className="mt-3 space-y-4">
      {depths.map((depth) => (
        <div key={depth}>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className={`text-xs ${depthBadgeClass(depth)}`}>
              {depthLabel(depth)}
            </Badge>
            <span className="text-xs text-muted-foreground">{byDepth[depth].length} member{byDepth[depth].length !== 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-1">
            {byDepth[depth].map((node) => (
              <div
                key={node.id}
                className="flex items-center gap-3 px-3 py-2 rounded-md bg-muted/40 text-sm"
              >
                <div className="w-6 text-center text-muted-foreground text-xs">{depth === 1 ? '└' : '·'}</div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium truncate">{displayName(node)}</span>
                  <span className="text-muted-foreground ml-1">@{node.username}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    {node.reviewsCount ?? 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <ShoppingBag className="w-3 h-3" />
                    {Number(node.orderCount ?? 0)}
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    {formatDollar(node.totalSpend)}
                  </span>
                  <span className="text-muted-foreground/60">
                    {new Date(node.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LeaderRow({
  leader,
  rank,
  expanded,
  onToggle,
}: {
  leader: ReferralLeader;
  rank: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border rounded-md overflow-hidden">
      <button
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover-elevate"
        onClick={onToggle}
      >
        <span className="text-sm font-semibold text-muted-foreground w-6 shrink-0">#{rank}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{displayName(leader)}</div>
          <div className="text-xs text-muted-foreground">@{leader.username}</div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground shrink-0">
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            <span className="font-medium text-foreground">{leader.networkSize}</span>
            <span>network</span>
          </span>
          <span className="flex items-center gap-1">
            <DollarSign className="w-3.5 h-3.5" />
            <span className="font-medium text-foreground">{formatDollar(leader.networkSpend)}</span>
          </span>
          <span className="flex items-center gap-1">
            <Star className="w-3.5 h-3.5" />
            <span className="font-medium text-foreground">{leader.networkReviews}</span>
            <span>reviews</span>
          </span>
          <span className="flex items-center gap-1">
            <ShoppingBag className="w-3.5 h-3.5" />
            <span className="font-medium text-foreground">{leader.networkOrders}</span>
            <span>orders</span>
          </span>
          {leader.engagementRate !== null && (
            <span className="flex items-center gap-1">
              <Activity className="w-3.5 h-3.5" />
              <span className="font-medium text-foreground">{leader.engagementRate}</span>
              <span>rate</span>
            </span>
          )}
        </div>
        <div className="shrink-0 ml-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t bg-muted/20">
          <div className="flex flex-wrap gap-3 pt-3 mb-2 text-xs text-muted-foreground sm:hidden">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{leader.networkSize} network</span>
            <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{formatDollar(leader.networkSpend)}</span>
            <span className="flex items-center gap-1"><Star className="w-3 h-3" />{leader.networkReviews} reviews</span>
            <span className="flex items-center gap-1"><ShoppingBag className="w-3 h-3" />{leader.networkOrders} orders</span>
          </div>
          <NetworkTree userId={leader.id} />
        </div>
      )}
    </div>
  );
}

export default function AdminReferralNetwork() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'network' | 'influencers'>('network');

  const { data: leaders, isLoading } = useQuery<ReferralLeader[]>({
    queryKey: ['/api/admin/referral-network-leaders'],
    queryFn: async () => {
      const _tok = localStorage.getItem('auth_token');
      const res = await fetch('/api/admin/referral-network-leaders', {
        headers: _tok && _tok !== 'null' && _tok !== 'undefined' ? { Authorization: `Bearer ${_tok}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load leaders');
      return res.json();
    },
  });

  const sortedByNetwork = leaders ? [...leaders].sort((a, b) => Number(b.networkSize) - Number(a.networkSize)) : [];
  const sortedByEngagement = leaders
    ? [...leaders].sort(
        (a, b) =>
          parseFloat(b.engagementRate ?? '0') - parseFloat(a.engagementRate ?? '0')
      )
    : [];

  const totalNetworkSize = sortedByNetwork.reduce((s, l) => s + Number(l.networkSize), 0);
  const totalNetworkSpend = sortedByNetwork.reduce((s, l) => s + parseFloat(l.networkSpend || '0'), 0);
  const totalNetworkReviews = sortedByNetwork.reduce((s, l) => s + Number(l.networkReviews), 0);

  const listToRender = activeTab === 'network' ? sortedByNetwork : sortedByEngagement;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Referral Network</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Who's growing the platform — top referrers by network size, spend, and engagement.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Network className="w-4 h-4" />
                Total Network Members
              </div>
              {isLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <div className="text-2xl font-bold">{totalNetworkSize.toLocaleString()}</div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <DollarSign className="w-4 h-4" />
                Total Network Spend
              </div>
              {isLoading ? (
                <Skeleton className="h-7 w-24" />
              ) : (
                <div className="text-2xl font-bold">{formatDollar(totalNetworkSpend)}</div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Star className="w-4 h-4" />
                Total Network Reviews
              </div>
              {isLoading ? (
                <Skeleton className="h-7 w-12" />
              ) : (
                <div className="text-2xl font-bold">{totalNetworkReviews.toLocaleString()}</div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Referrers</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Tabs
              value={activeTab}
              onValueChange={(v) => {
                setActiveTab(v as 'network' | 'influencers');
                setExpandedId(null);
              }}
              className="mb-4"
            >
              <TabsList>
                <TabsTrigger value="network" className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  By Network Size
                </TabsTrigger>
                <TabsTrigger value="influencers" className="flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" />
                  Top Influencers
                </TabsTrigger>
              </TabsList>

              <TabsContent value="network" className="mt-4 space-y-2">
                {isLoading
                  ? [...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
                  : sortedByNetwork.length === 0
                  ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No referral data yet.</p>
                      <p className="text-xs mt-1">Users with referral conversions will appear here.</p>
                    </div>
                  )
                  : sortedByNetwork.map((leader, i) => (
                    <LeaderRow
                      key={leader.id}
                      leader={leader}
                      rank={i + 1}
                      expanded={expandedId === leader.id}
                      onToggle={() => setExpandedId(expandedId === leader.id ? null : leader.id)}
                    />
                  ))}
              </TabsContent>

              <TabsContent value="influencers" className="mt-4 space-y-2">
                {isLoading
                  ? [...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
                  : sortedByEngagement.length === 0
                  ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No engagement data yet.</p>
                    </div>
                  )
                  : sortedByEngagement.map((leader, i) => (
                    <LeaderRow
                      key={leader.id}
                      leader={leader}
                      rank={i + 1}
                      expanded={expandedId === leader.id}
                      onToggle={() => setExpandedId(expandedId === leader.id ? null : leader.id)}
                    />
                  ))}
                <p className="text-xs text-muted-foreground pt-2 text-center">
                  Ranked by engagement rate: (reviews + orders) per network member. Identifies high-quality referrers vs. large but inactive networks.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
